"use client";

import { useCallback, useEffect, useState, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/lib/hooks/useAuth";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";
import {
  getSupportCategoryLabel,
  INQUIRY_STATUS_LABEL,
  type InquiryStatus,
} from "@/lib/config/customerSupport";
import { InlineContactForm } from "@/components/contact/InlineContactForm";

interface MyInquiry {
  id: string;
  subject: string;
  message: string;
  category: string;
  status: InquiryStatus;
  audience?: string;
  adminReply?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const STATUS_STYLE: Record<InquiryStatus, string> = {
  pending: "bg-rose-50 text-rose-700",
  in_progress: "bg-amber-50 text-amber-700",
  resolved: "bg-emerald-50 text-emerald-700",
  refunded: "bg-sky-50 text-sky-700",
};

type Tab = "list" | "compose";

function MyInquiriesContent() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [tab, setTab] = useState<Tab>(
    tabParam === "compose" || tabParam === "contact" ? "compose" : "list"
  );
  const [items, setItems] = useState<MyInquiry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tabParam === "compose" || tabParam === "contact") {
      setTab("compose");
    } else if (tabParam === "list") {
      setTab("list");
    }
  }, [tabParam]);

  const setActiveTab = (next: Tab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams.toString());
    if (next === "compose") params.set("tab", "compose");
    else params.delete("tab");
    const qs = params.toString();
    router.replace(qs ? `/profile/inquiries?${qs}` : "/profile/inquiries", {
      scroll: false,
    });
  };

  const load = useCallback(async () => {
    if (!user?.uid) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = await getFirebaseAuth()?.currentUser?.getIdToken();
      if (!token) throw new Error("로그인이 필요합니다.");

      const response = await fetch("/api/contact/my", {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "조회 실패");
      setItems(data.contacts || []);
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "문의 내역을 불러오지 못했습니다."
      );
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!authLoading) void load();
  }, [authLoading, load]);

  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-14 text-center">
        <p className="text-sm text-gray-600">
          로그인 후 문의 내역을 확인할 수 있습니다.
        </p>
        <Link
          href="/auth/login?redirect=/profile/inquiries"
          className="mt-4 inline-flex rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white"
        >
          로그인하기
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-950">문의</h1>
        <p className="mt-1 text-sm text-gray-500">
          1:1 문의 작성과 접수 내역을 한곳에서 확인합니다.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setActiveTab("list")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
            tab === "list"
              ? "bg-gray-900 text-white"
              : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
          }`}
        >
          문의 내역
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("compose")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
            tab === "compose"
              ? "bg-gray-900 text-white"
              : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
          }`}
        >
          1:1 문의
        </button>
        {tab === "list" && (
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            새로고침
          </button>
        )}
      </div>

      {tab === "compose" ? (
        <InlineContactForm
          audience="customer"
          onSubmitted={() => {
            void load();
            toast.success("문의가 접수되었습니다.");
          }}
        />
      ) : loading ? (
        <div className="flex min-h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white py-14 text-center">
          <p className="text-sm text-gray-500">아직 접수한 문의가 없습니다.</p>
          <button
            type="button"
            onClick={() => setActiveTab("compose")}
            className="mt-4 inline-flex rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white"
          >
            1:1 문의하기
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <ul className="divide-y divide-gray-100">
            {items.map(item => {
              const status = (item.status || "pending") as InquiryStatus;
              return (
                <li key={item.id} className="px-4 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        STATUS_STYLE[status] || STATUS_STYLE.pending
                      }`}
                    >
                      {INQUIRY_STATUS_LABEL[status] || status}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {getSupportCategoryLabel(item.category)}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("ko-KR")
                        : ""}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-900">
                    {item.subject || "(제목 없음)"}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">
                    {item.message}
                  </p>
                  {item.adminReply && (
                    <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5">
                      <p className="text-[11px] font-semibold text-gray-500">
                        답변
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                        {item.adminReply}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function MyInquiriesPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
        </div>
      }
    >
      <MyInquiriesContent />
    </Suspense>
  );
}
